// React
import { memo, useCallback } from "react";

// UI
import { Button } from "ui/components/Button/Button";
import { MultiDropdown } from "ui/components/Dropdown/MultiDropdown";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextField } from "ui/components/TextField/TextField";

// Tipovi
import { PricingCombinationRowProps } from "./types";

/**
 * Jedan redak matrice. Zaseban je zbog brisanja — indeks se zatvara ovdje, pa gumb ne treba
 * callback složen u JSX-u.
 */
const PricingCombinationRow: React.FC<PricingCombinationRowProps> = (
  props: PricingCombinationRowProps
) => {
  const {
    index,
    combination,
    packOptions,
    errors,
    onPacksChange,
    onDescriptionChange,
    onPriceChange,
    onRemove,
  } = props;

  const handleRemoveClick = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  // Yup putanje za redak niza glase `combinations[0].price`.
  const packCodesError = errors[`combinations[${index}].packCodes`];
  const priceError = errors[`combinations[${index}].price`];
  const rowErrors = [packCodesError, priceError].filter(Boolean);

  return (
    /*
      Svaka kombinacija je svoja kartica. Bez okvira se dva retka po kombinaciji stapaju u
      jedan niz polja, pa se ne vidi gdje jedna staje a druga počinje.
    */
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-semibold text-slate-600">Kombinacija {index + 1}</p>

        <Button variant="outlined" children="Ukloni" onClick={handleRemoveClick} />
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1">
          <MultiDropdown
            name={`combination-packs-${index}`}
            label="Paket"
            value={combination.packCodes}
            options={packOptions}
            onChange={onPacksChange}
            error={packCodesError}
            width="full"
          />
        </div>

        <div className="w-[180px]">
          <NumberField
            name={`combination-price-${index}`}
            label="€ / vozilo / mj"
            value={combination.price}
            onChange={onPriceChange}
            error={priceError}
            decimals={2}
            size="full"
          />
        </div>
      </div>

      {/* Opis stoji u svom retku: rečenica treba širine, a uz packove i cijenu je ne bi imala. */}
      <div className="mt-3">
        <TextField
          type="text"
          name={`combination-description-${index}`}
          label="Kratki opis"
          placeholder="Kome je paket namijenjen"
          value={combination.description ?? ""}
          onChange={onDescriptionChange}
          width="full"
        />
      </div>

      {/* Polja greške pokazuju samo kroz tooltip, pa se ovdje ispisuje i tekst. */}
      {rowErrors.length > 0 && (
        <p className="text-sm text-red-600 mt-2">{rowErrors.join(". ")}</p>
      )}
    </div>
  );
};

export default memo(PricingCombinationRow);
